/**
 * AtlasRiverModule — RIVER RIBBONS + WATERFALL SHEETS (terrain wave 2026-09-08).
 *
 * The world hydrology bake carves every channel into the ground; this module
 * draws the water in them. Around the camera (a quality-tier radius) every
 * river polyline within reach becomes a triangle strip at its water surface
 * (two vertices per point, width = channel width + a lip so the ribbon meets
 * the cut bank), and every waterfall reach gets a NAPPE — a subdivided sheet
 * down the free-fall parabola from the lip — a foam ring on the pool and
 * spray / mist / crest PARTICLES (2026-09-11). One geometry per kind,
 * rebuilt when the camera moves REBUILD_M from the last build centre.
 *
 * Materials: the RIBBON wears the engine's realistic water — THE SAME WATER
 * AS THE SEA AND THE LAKES (2026-09-08, "the rivers seem a different type of
 * water") — in STRIP mode: bed depth from the `bedDepth` vertex attribute
 * (a ribbon has no bake footprint), detail normals advected along the strip's
 * metric uv (u = metres downstream, v = metres across), a 2 cm chop kept out
 * of the vertex stage (a ribbon must stay in its channel), no waterline
 * discard (the lip still meets a coarse cut bank), no reflector. The sheets
 * and mist keep their scrolled normal-map look (Vlachos 2010 flow map).
 * All use the engine's generated water normal map, no assets.
 *
 * Levers: window.__rivers() → counts and the last build; localStorage
 * 'atlas:rivers' = 'off' hides the layer.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { applySoftParticles } from 'enginex/vfx/SoftParticleModifier.js';
import { waterFx, stampWater } from '../lib/water_fx.js';
import { plungeStamp } from '../lib/water_physics.js';
import { softHandles, stage3Stats } from '../lib/stage3_fx.js';
import { normalMap, texture as tslTexture, uv, uniform, vec2, vec3, float, mix, sin, fract, smoothstep } from 'three/tsl';
import { GpuParticleEmitter, CpuParticleAdapter } from 'enginex/particles/gpu';
import type { App, IModule } from 'enginex';
import { generateWaterNormalMap } from 'enginex/environment/WaterSystem.js';
import { createRealisticWaterMaterial, DEFAULT_REALISTIC_WATER, type RealisticWaterHandles } from 'enginex/environment/water/RealisticWaterMaterial.js';
import { createWaveSet } from 'enginex/environment/water/GerstnerWaves.js';
import { uSunDir } from './SkyModule.js';
import { waterIlluminance, waterSkyTint } from '../lib/water_lighting.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { activePreset } from '../lib/quality_tiers.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { loadHydrology, loadedRivers } from '../lib/hydrology_load.js';
import { RIVER_STRIDE, RIVER_FLAG_FALL, RIVER_FLAG_LAKE, RIVER_FLAG_NORAISE } from '../lib/hydrology_bake.js';
import type { RiverSample } from '../lib/audio/water_audio.js';
import { ribbonRows, nappeCurve, fallFxRecipe, reachWater, fallBreak, NAPPE_COLS, NAPPE_DIP, type FallFxRecipe } from '../lib/river_geometry.js';

const TICK_EVERY = 20;
/** Rebuild when the camera has moved this far from the last build centre. */
const REBUILD_M = 400;
/** Ribbon lip beyond the channel half-width (m) so the water meets the cut bank. */
const RIBBON_LIP = 0.7;
/** Ribbon sits this far above the water level (fights z with the bed, never the bank). */
const RIBBON_LIFT = 0.04;
/** Downstream speed of the surface pattern (m/s) — the engine advects the detail normals along u. */
const RIVER_FLOW_MPS = 1.1;
/** Water depth (m) carried by the ribbon's edge vertices: shallow, so the surf-foam band hugs the banks. */
const RIBBON_EDGE_DEPTH = 0.25;
/** Shoal band of the river (m): a bank fringe — a 2 m channel must read as deep water like the lake. */
const RIVER_SHOAL_DEPTH = 1.1;
/** Waterfall particles (spray + mist + crest) run for the nearest falls inside this radius (m). */
const FX_RADIUS = 260;
/** At most this many falls carry particles at once. */
const FX_MAX = 6;
/** Re-pick the particle falls every N frames (the player moves). */
const FX_TICK = 30;
/** The water leaves a lip at ~this speed (m/s) — the free-fall parabola's v0. */
const NAPPE_V0 = 2.4;

/** One waterfall in the current build (the sheet's landing is where the particles live). */
interface FallSite {
  key: string;
  /** The plunge point (the sheet's landing on the pool). */
  x: number; y: number; z: number;
  /** The lip. */
  lipX: number; lipY: number; lipZ: number;
  /** Unit downstream direction. */
  dx: number; dz: number;
  drop: number; width: number;
  /** The reach (for the harness's ledge samples). */
  ax: number; az: number; bx: number; bz: number; wa: number; wb: number; len: number; depthA: number; depthB: number;
}
type Emitter = GpuParticleEmitter | CpuParticleAdapter;
interface FallFx { key: string; site: FallSite; spray: Emitter; mist: Emitter; crest: Emitter; recipe: FallFxRecipe }

interface Build { cx: number; cz: number; points: number; falls: number; radius: number }

export class AtlasRiverModule implements IModule {
  readonly name = 'AtlasRiverModule';
  private scene: THREE.Scene | null = null;
  private app: App | null = null;
  private seed = '';
  private ribbon: THREE.Mesh | null = null;
  private sheets: THREE.Mesh | null = null;
  /** The foam ring on the pool at every fall's foot. */
  private foam: THREE.Mesh | null = null;
  private fallSites: FallSite[] = [];
  private fx: FallFx[] = [];
  private fxTick = 0;
  private readonly fxPos = new THREE.Vector3();
  private readonly fxQuat = new THREE.Quaternion();
  private readonly fxUp = new THREE.Vector3(0, 1, 0);
  private readonly fxDir = new THREE.Vector3();
  private readonly fxScale = new THREE.Vector3(1, 1, 1);
  private readonly fxZero = new THREE.Vector3(0, 0, 0);
  private uTime = uniform(0);
  private frame = 0;
  private last: Build | null = null;
  private normalTex: THREE.DataTexture | null = null;
  private riverHandles: RealisticWaterHandles | null = null;
  private simpleMat: THREE.Material | null = null;
  /** The river's look for the probe: reflection parity, shoal band, ripple scale, lake hand-off. */
  private look: { reflection: boolean; shoalDepth: number; waveLength: number; flowSpeed: number; hiddenLakeSegments: number } | null = null;
  private mirrorTick = 0;
  private readonly skyTint = new THREE.Color();
  private t0 = performance.now();

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene as unknown as THREE.Scene;
    this.build();
    app.world.addSystem(makeSystem('AtlasRivers', Phase.RenderSub, 41, (dt) => this.tick(dt)));
    (globalThis as unknown as { __rivers?: () => unknown }).__rivers = () => ({
      seed: this.seed, last: this.last, visible: this.ribbon?.visible ?? false,
      material: this.ribbon && this.simpleMat && this.ribbon.material === this.simpleMat ? 'simple' : 'realistic',
      nearest: this.nearestPoint(),
      pointsNear: (radius: number) => this.pointsNear(radius),
      look: this.look,
      reflection: this.reflectionState(),
      ribbonFacing: () => this.ribbonFacing(),
      rebuildNow: () => { const rv = this.seed ? loadedRivers(this.seed) : null; if (rv) this.rebuild(rv, atlas.localX, atlas.localZ, Math.max(1200, Math.min(5000, activePreset().farCull ?? 2000))); return this.look; },
      ribbonVerts: (this.ribbon?.geometry.attributes.position as THREE.BufferAttribute | undefined)?.count ?? 0,
      sheetVerts: (this.sheets?.geometry.attributes.position as THREE.BufferAttribute | undefined)?.count ?? 0,
      falls: () => this.fallState(),
      fallSamples: (radius: number) => this.fallSamples(radius),
    });
  }

  /** The nearest river point to the player (probes) — world x/z, water level, width. */
  private nearestPoint(): { x: number; z: number; water: number; width: number; d: number } | null {
    const rivers = this.seed ? loadedRivers(this.seed) : null;
    if (!rivers) return null;
    let best: { x: number; z: number; water: number; width: number; d: number } | null = null;
    for (const r of rivers) {
      for (let k = 0; k < r.length; k += RIVER_STRIDE) {
        const d = Math.hypot(r[k] - atlas.localX, r[k + 1] - atlas.localZ);
        if (!best || d < best.d) best = { x: r[k], z: r[k + 1], water: r[k + 2], width: r[k + 3], d };
      }
    }
    return best;
  }

  /**
   * River points within `radius` m of the player (probes): world x/z, water,
   * width, depth and the segment's unit direction — the fit probe samples the
   * chunk mesh at the centre and at the ribbon's edges.
   */
  private pointsNear(radius: number): Array<{ x: number; z: number; water: number; width: number; depth: number; flags: number; dx: number; dz: number; d: number }> {
    const rivers = this.seed ? loadedRivers(this.seed) : null;
    if (!rivers) return [];
    const out: Array<{ x: number; z: number; water: number; width: number; depth: number; flags: number; dx: number; dz: number; d: number }> = [];
    for (const r of rivers) {
      for (let k = 0; k < r.length; k += RIVER_STRIDE) {
        const d = Math.hypot(r[k] - atlas.localX, r[k + 1] - atlas.localZ);
        if (d > radius) continue;
        const n = k + RIVER_STRIDE < r.length ? k + RIVER_STRIDE : k - RIVER_STRIDE;
        const len = Math.hypot(r[n] - r[k], r[n + 1] - r[k + 1]) || 1;
        out.push({ x: r[k], z: r[k + 1], water: r[k + 2], width: r[k + 3], depth: r[k + 4], flags: r[k + 5], dx: (r[n] - r[k]) / len, dz: (r[n + 1] - r[k + 1]) / len, d });
      }
    }
    return out.sort((p, q) => p.d - q.d);
  }

  // ── for the water-audio module (AtlasWaterAudioModule) ──
  /** The waterfalls of the current build (plunge point, drop, width) — the sound's emitters. */
  soundFalls(): ReadonlyArray<{ key: string; x: number; y: number; z: number; drop: number; width: number }> { return this.fallSites; }
  /**
   * Every river polyline with a point within `radius` of (px, pz), as runs of consecutive samples — what the SPLINE
   * EMITTER slides along. Scans the whole network (103k points on the Commons): the caller re-asks only after moving
   * a few tens of metres, never per frame. A still reach (on a lake, at the sea) is flagged: it is not a source.
   */
  riverLinesNear(px: number, pz: number, radius: number): RiverSample[][] {
    const rivers = this.seed ? loadedRivers(this.seed) : null;
    if (!rivers) return [];
    const out: RiverSample[][] = [];
    const r2 = radius * radius;
    for (const r of rivers) {
      let run: RiverSample[] | null = null;
      for (let k = 0; k < r.length; k += RIVER_STRIDE) {
        const dx = r[k] - px, dz = r[k + 1] - pz;
        if (dx * dx + dz * dz > r2) { if (run && run.length > 1) out.push(run); run = null; continue; }
        const flags = r[k + 5];
        (run ??= []).push({ x: r[k], z: r[k + 1], water: r[k + 2], width: r[k + 3], still: (flags & (RIVER_FLAG_NORAISE | RIVER_FLAG_LAKE)) !== 0 });
      }
      if (run && run.length > 1) out.push(run);
    }
    return out;
  }

  /**
   * The planar mirror is one plane; a river descends. Like the lake mirror it
   * is seated at the water level of the reach nearest the player every few
   * frames — exact for the water in front of the camera, approximate beyond.
   */
  private seatRiverMirror(): void {
    const target = this.riverHandles?.reflectorTarget;
    if (!target || !this.ribbon) return;
    // nearestPoint() scans every river point (103k on the Commons): rescan
    // only once the player has moved 20 m from the last seat (2026-09-12).
    if (this.mirrorSeat && Math.hypot(atlas.localX - this.mirrorSeat.x, atlas.localZ - this.mirrorSeat.z) < 20) return;
    this.mirrorSeat = { x: atlas.localX, z: atlas.localZ };
    const near = this.nearestPoint();
    if (near) target.position.y = near.water + RIBBON_LIFT;
  }
  private mirrorSeat: { x: number; z: number } | null = null;

  /** Planar-reflection diagnostics for the probe (null when reflections are off): the mirror plane must sit at the water, facing up. */
  private reflectionState(): { parented: boolean; normalY: number; planeY: number; hasOutput: boolean; virtualCamY: number | null } | null {
    const h = this.riverHandles;
    if (!h || !h.reflectorTarget || !this.ribbon) return null;
    const t = h.reflectorTarget;
    t.updateWorldMatrix(true, false);
    const n = new THREE.Vector3(0, 0, 1).transformDirection(t.matrixWorld);
    const p = new THREE.Vector3(); t.getWorldPosition(p);
    const base = h.reflectorNode?.reflector as unknown as { hasOutput?: boolean; virtualCameras?: WeakMap<object, THREE.Camera> } | undefined;
    const cam = this.app?.renderer.cameras.main as THREE.Camera | undefined;
    const vc = cam && base?.virtualCameras?.get(cam);
    const shift = this.scene?.position.y ?? 0; // FP64 rebase: scene.position = −origin
    return { parented: t.parent === this.ribbon, normalY: Math.round(n.y * 1000) / 1000, planeY: Math.round((p.y - shift) * 100) / 100, hasOutput: base?.hasOutput ?? false, virtualCamY: vc ? Math.round((vc.position.y - shift) * 100) / 100 : null };
  }

  /** Mean geometric normal y of the ribbon's first triangles (probes): must be > 0 — water faces up. */
  private ribbonFacing(): number | null {
    const g = this.ribbon?.geometry;
    const p = g?.attributes.position as THREE.BufferAttribute | undefined;
    const ix = g?.index;
    if (!p || !ix || ix.count < 3) return null;
    const A = new THREE.Vector3(), B = new THREE.Vector3(), C = new THREE.Vector3(), N = new THREE.Vector3();
    let sum = 0, n = 0;
    for (let i = 0; i + 2 < Math.min(ix.count, 600); i += 3) {
      A.fromBufferAttribute(p, ix.getX(i)); B.fromBufferAttribute(p, ix.getX(i + 1)); C.fromBufferAttribute(p, ix.getX(i + 2));
      N.subVectors(B, A).cross(C.clone().sub(A));
      if (N.lengthSq() > 1e-9) { sum += N.normalize().y; n++; }
    }
    return n ? sum / n : null;
  }

  static optedOut(): boolean {
    try { return localStorage.getItem('atlas:rivers') === 'off'; } catch { return false; }
  }

  private build(): void {
    if (!this.scene) return;
    this.normalTex = generateWaterNormalMap(256);
    this.normalTex.wrapS = this.normalTex.wrapT = THREE.RepeatWrapping;
    // ── Ribbon material: flow along u, two phase-offset samples cross-faded ──
    const mkFlow = (speed: number, dir: [number, number], scaleAlong: number, scaleAcross: number, foam: number, tint: [number, number, number], foamRamp?: [number, number]): THREE.MeshStandardNodeMaterial => {
      const m = new THREE.MeshStandardNodeMaterial();
      const tex = this.normalTex as THREE.Texture;
      const t = this.uTime;
      const d = vec2(dir[0], dir[1]);
      const uvA = uv().mul(vec2(scaleAlong, scaleAcross)).add(d.mul(t.mul(speed)));
      const uvB = uv().mul(vec2(scaleAlong * 1.37, scaleAcross * 1.37)).add(d.mul(t.mul(speed * 0.71))).add(vec2(0.31, 0.57));
      const phase = fract(t.mul(0.5));
      const w = smoothstep(0.0, 1.0, sin(phase.mul(Math.PI)));
      const nA = tslTexture(tex, uvA).xyz;
      const nB = tslTexture(tex, uvB).xyz;
      m.normalNode = normalMap(mix(nA, nB, w), vec2(0.55, 0.55));
      const base = vec3(tint[0], tint[1], tint[2]);
      // Foam: a slow second scroll of the map's red channel lifts toward white.
      const f = tslTexture(tex, uv().mul(vec2(scaleAlong * 0.35, scaleAcross * 0.35)).add(d.mul(t.mul(speed * 0.4)))).x;
      // A waterfall whitens toward its foot (the nappe aerates as it falls): the foam ramps along v (metres down).
      const foamK = foamRamp ? float(foam).mul(float(0.35).add(smoothstep(foamRamp[0], foamRamp[1], uv().y))) : float(foam);
      m.colorNode = mix(base, vec3(0.86, 0.9, 0.92), smoothstep(0.55, 0.95, f).mul(foamK));
      m.roughnessNode = float(0.14);
      m.metalness = 0;
      m.transparent = true;
      m.opacity = 0.84;
      m.depthWrite = false;
      m.side = THREE.DoubleSide;
      return m;
    };
    // ── Ribbon: the engine's realistic water in STRIP mode (see the header).
    // ONE WATER LOOK (2026-09-11, "the river looks different from the other
    // water"): the river now wears exactly the lake's look — the lake's calm
    // 5 cm / 7 m ripples (the 2 cm / 1.6 m chop read as a different, finer
    // water), the same planar reflection when the preset opts in (a flat sky
    // colour beside a mirrored sky was the biggest tell), and a shoal band
    // that is a BANK FRINGE (1.1 m) instead of a 2.5 m band that swallowed a
    // 2 m channel whole and mud-tinted every ribbon. The cutoff keeps every
    // wavelength out of the vertex stage (a ribbon must stay in its channel).
    const riverWaves = createWaveSet({ amplitude: 0.05, wavelength: 7, count: 4, steepness: 0.35, seed: 73 });
    const reflect = activePreset().waterReflections ?? false;
    this.riverHandles = createRealisticWaterMaterial({
      ...DEFAULT_REALISTIC_WATER,
      normalMap: this.normalTex,
      // WebGPU doctrine: no viewport scene copies (the sea's rule).
      screenSpaceRefraction: !(this.app?.renderer.isWebGPU ?? true),
      // The same menu opt-in as the sea and the lakes (one more scene render
      // per frame when on; bounces:false, never nested). Seated at the nearest
      // reach's water level every few frames (seatRiverMirror).
      reflection: reflect,
      skyColor: 0x87a8c3,
      causticIntensity: 0.3,
      vertexWavelengthCutoff: 1e6,
      shoreline: { depthAttribute: 'bedDepth', encodeMax: 1, shoalDepth: RIVER_SHOAL_DEPTH, foamDepth: 0.35, shallowTint: 0x9a8c66 },
      flow: { speed: RIVER_FLOW_MPS },
      // Interactive water: the shared camera-following field + rain rings.
      interaction: { texture: waterFx().texture, size: waterFx().size },
      rainRipples: true,
    }, riverWaves);
    this.look = { reflection: reflect, shoalDepth: RIVER_SHOAL_DEPTH, waveLength: 7, flowSpeed: RIVER_FLOW_MPS, hiddenLakeSegments: 0 };
    // The SIMPLE river (the minimal tier, or Graphics → River water): the old
    // scrolled-normal-map look on the same metric ribbon (7 m tiles), a live switch.
    this.simpleMat = mkFlow(0.22, [-1, 0], 1 / 7, 1 / 7, 0.35, [0.09, 0.26, 0.31]);
    const ribbonMat = activePreset().riverWater === 'simple' ? this.simpleMat : this.riverHandles.material;
    // THE SHEET: the nappe's uv is METRIC — u across (m), v DOWN the fall from
    // the lip (0 at the lip). The scroll `uv + d·t` with d = (0, −1) moves the
    // pattern toward +v, i.e. DOWN. (The old quad put v = 0 at the FOOT, so the
    // same scroll ran the water up the fall — "the direction of the water is
    // going up the waterfall".) Long streaks: 3.2 m along, 4 m across.
    const sheetMat = mkFlow(2.6, [0, -1], 1 / 4, 1 / 3.2, 1.1, [0.6, 0.72, 0.77], [0.5, 9]);
    sheetMat.opacity = 0.92;
    // THE FOAM RING on the pool at the foot (aerated water where the nappe
    // lands): uv.x = radial fraction (0 centre → 1 rim), uv.y = angle. The
    // noise scrolls OUTWARD (features move −d = +x) and fades to the rim.
    const foamMat = new THREE.MeshBasicNodeMaterial();
    {
      const t = this.uTime;
      const n = tslTexture(this.normalTex as THREE.Texture, uv().mul(vec2(1.6, 4)).add(vec2(t.mul(-0.22), t.mul(0.03)))).x;
      const rim = float(1).sub(uv().x);
      foamMat.colorNode = vec3(0.9, 0.94, 0.96);
      foamMat.opacityNode = smoothstep(0.32, 0.8, n).mul(rim.mul(rim)).mul(0.85);
      foamMat.transparent = true;
      foamMat.depthWrite = false;
      foamMat.side = THREE.DoubleSide;
      markMrtOverlay(foamMat); // foam floats ON the pool; the pool keeps its normal
    }
    const empty = (): THREE.BufferGeometry => {
      const g = new THREE.PlaneGeometry(1, 1, 1, 1);
      g.rotateX(-Math.PI / 2);
      return g;
    };
    this.ribbon = new THREE.Mesh(empty(), ribbonMat);
    // The mirror plane rides the ribbon (the target arrives pre-rotated); its y is seated per frame.
    if (this.riverHandles.reflectorTarget) this.ribbon.add(this.riverHandles.reflectorTarget);
    this.ribbon.name = 'CommonsRivers';
    this.ribbon.frustumCulled = true; // perf frontier: bounds are recomputed per rebuild
    this.ribbon.visible = false;
    this.ribbon.renderOrder = 2;
    this.sheets = new THREE.Mesh(empty(), sheetMat);
    this.sheets.name = 'CommonsWaterfalls';
    this.sheets.frustumCulled = true;
    this.sheets.visible = false;
    this.sheets.renderOrder = 3;
    this.foam = new THREE.Mesh(empty(), foamMat);
    this.foam.name = 'CommonsWaterfallFoam';
    this.foam.frustumCulled = true;
    this.foam.visible = false;
    this.foam.renderOrder = 4;
    for (const m of [this.ribbon, this.sheets, this.foam]) { m.matrixAutoUpdate = false; m.updateMatrix(); } // static world-space meshes
    this.scene.add(this.ribbon, this.sheets, this.foam);
  }

  private commonsSeed(): string | null {
    const conn = atlas.conn;
    if (!conn) return null;
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return null;
  }

  private hide(): void {
    if (this.ribbon) this.ribbon.visible = false;
    if (this.sheets) this.sheets.visible = false;
    if (this.foam) this.foam.visible = false;
    this.fallSites = [];
    this.clearFx();
    this.last = null;
  }

  private tick(dt: number): void {
    this.uTime.value = (performance.now() - this.t0) / 1000;
    this.stepFx(dt);
    if (this.ribbon && this.simpleMat && this.riverHandles) {
      // Graphics → River water, live: the ribbon swaps between the two materials.
      const wantSimple = activePreset().riverWater === 'simple';
      const isSimple = this.ribbon.material === this.simpleMat;
      if (wantSimple !== isSimple) this.ribbon.material = wantSimple ? this.simpleMat : this.riverHandles.material;
    }
    if (this.riverHandles) {
      // The river follows the sun exactly like the sea and the lakes.
      const h = this.riverHandles;
      const sun = uSunDir.value as THREE.Vector3;
      h.uTime.value = this.uTime.value;
      h.uSunDirection.value.copy(sun);
      h.uIlluminance.value = waterIlluminance(sun.y);
      h.uSkyColor.value.copy(waterSkyTint(sun.y, this.skyTint));
      const wfx = waterFx();
      h.uInteractionCenter.value.set(wfx.centerRender.x, wfx.centerRender.z);
      h.uRainIntensity.value = wfx.rain;
      if (++this.mirrorTick % 15 === 0) this.seatRiverMirror();
    }
    if (++this.frame % TICK_EVERY !== 0) return;
    if (atlas.currentWorldId === null || atlas.currentWorldId !== atlas.commonsWorldId || AtlasRiverModule.optedOut()) { this.hide(); return; }
    if (!this.seed) {
      const s = this.commonsSeed();
      if (!s) return;
      this.seed = s;
      void loadHydrology(s);
    }
    const rivers = loadedRivers(this.seed);
    if (!rivers) return;
    const px = atlas.localX, pz = atlas.localZ;
    const radius = Math.max(1200, Math.min(5000, activePreset().farCull ?? 2000));
    if (this.last && this.last.radius === radius && Math.hypot(px - this.last.cx, pz - this.last.cz) < REBUILD_M) return;
    this.rebuild(rivers, px, pz, radius);
  }

  private rebuild(rivers: number[][], cx: number, cz: number, radius: number): void {
    if (!this.ribbon || !this.sheets || !this.foam) return;
    const reach = radius + 300;
    const r2 = reach * reach;
    const pos: number[] = [], uvs: number[] = [], idx: number[] = [], bed: number[] = [];
    const sPos: number[] = [], sUv: number[] = [], sIdx: number[] = [];
    const fPos: number[] = [], fUv: number[] = [], fIdx: number[] = [];
    const sites: FallSite[] = [];
    let points = 0, falls = 0, hiddenLake = 0;
    for (const r of rivers) {
      const m = r.length / RIVER_STRIDE;
      // Runs of consecutive in-reach points (with one point of slack each side).
      let run: number[] = [];
      const flush = (): void => {
        if (run.length >= 2) { points += run.length; this.strip(r, run, pos, uvs, idx, bed); }
        run = [];
      };
      for (let k = 0; k < m; k++) {
        const x = r[k * RIVER_STRIDE], z = r[k * RIVER_STRIDE + 1];
        // ON A LAKE the lake renders the water (Crest's clip-surface rule — no
        // double sheet, no dark ribbon lying across the lake): a SEGMENT whose
        // two points are both on the lake is not drawn; the entry and exit
        // segments stay so the river runs INTO the lake and out of it. (A rule
        // on points — hidden when both neighbours are on the lake — never fired
        // on a two-point crossing, and the user's screenshot was that ribbon.)
        const lakeAt = (i: number): boolean => i >= 0 && i < m && (r[i * RIVER_STRIDE + 5] & RIVER_FLAG_LAKE) !== 0;
        const inReach = (x - cx) * (x - cx) + (z - cz) * (z - cz) < r2;
        if (inReach) {
          if (lakeAt(k) && lakeAt(k - 1)) { if (run.length) { flush(); hiddenLake++; } }
          else if (run.length === 0 && k > 0) run.push(k - 1);
          run.push(k);
          if (k < m - 1 && (r[k * RIVER_STRIDE + 5] & RIVER_FLAG_FALL)) { falls++; sites.push(this.nappe(r, k, sPos, sUv, sIdx, fPos, fUv, fIdx)); }
        } else if (run.length) { run.push(k); flush(); }
      }
      flush();
    }
    if (this.look) this.look.hiddenLakeSegments = hiddenLake;
    const swap = (mesh: THREE.Mesh, p: number[], u: number[], i: number[], extra?: { name: string; data: number[]; size: number }): void => {
      const old = mesh.geometry;
      if (p.length === 0) { mesh.visible = false; return; }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(p, 3));
      g.setAttribute('uv', new THREE.Float32BufferAttribute(u, 2));
      if (extra) g.setAttribute(extra.name, new THREE.Float32BufferAttribute(extra.data, extra.size));
      g.setIndex(i);
      g.computeVertexNormals();
      g.computeBoundingSphere();
      mesh.geometry = g;
      mesh.visible = true;
      disposeLater(old);
    };
    swap(this.ribbon, pos, uvs, idx, { name: 'bedDepth', data: bed, size: 1 });
    swap(this.sheets, sPos, sUv, sIdx);
    swap(this.foam, fPos, fUv, fIdx);
    this.fallSites = sites;
    this.syncFx();
    this.last = { cx, cz, points, falls, radius };
  }

  /**
   * Triangle strip for points run[0..] of river r at their water surface —
   * THREE vertices across (bank, centre, bank) so the bed depth can be the
   * channel depth mid-stream and shallow at the lip; uv is METRIC (u = metres
   * downstream, v = metres across) for the engine's strip advection. The rows
   * come from `ribbonRows`: a waterfall reach holds the upstream level to the
   * LIP and resumes at the FOOT — no strip in between (the sheet covers the
   * drop). A straight strip from point to point cut through the carve's ledge
   * (under the ground before the lip, in the air after it — the floating
   * water of the user's screenshots).
   */
  private strip(r: number[], run: number[], pos: number[], uvs: number[], idx: number[], bed: number[]): void {
    const rows = ribbonRows(r, run, RIBBON_LIP);
    const base = pos.length / 3;
    let arc = 0;
    for (let i = 0; i < rows.length; i++) {
      const w = rows[i];
      const x = w.x, z = w.z, y = w.y + RIBBON_LIFT, hw = w.hw;
      const nx = -w.tz, nz = w.tx;
      if (i > 0) arc += Math.hypot(x - rows[i - 1].x, z - rows[i - 1].z);
      const depth = Math.max(RIBBON_EDGE_DEPTH, w.depth);
      pos.push(x + nx * hw, y, z + nz * hw, x, y, z, x - nx * hw, y, z - nz * hw);
      uvs.push(arc, -hw, arc, 0, arc, hw);
      bed.push(RIBBON_EDGE_DEPTH, depth, RIBBON_EDGE_DEPTH);
      if (i > 0 && w.joined) {
        const a = base + (i - 1) * 3, b = base + i * 3;
        // Wound to face UP like the sea and the lake planes (counter-clockwise
        // seen from above): the first cut faced DOWN, so from above the ribbon
        // was a back face of the double-sided water and drew its Snell's-window
        // underside — the black river of 2026-09-11 (pale only from below).
        idx.push(a, b, a + 1, a + 1, b, b + 1, a + 1, b + 1, a + 2, a + 2, b + 1, b + 2);
      }
    }
  }

  /**
   * The waterfall for the fall reach starting at point k: the NAPPE — a
   * subdivided sheet down the free-fall parabola from the lip (v0 ≈ the
   * river's speed, throttled to land inside the carve's ledge run), dipping
   * under the pool so its seam hides in the foam — and the FOAM RING on the
   * pool where it lands. Returns the site the particles ride.
   */
  private nappe(r: number[], k: number, sPos: number[], sUv: number[], sIdx: number[], fPos: number[], fUv: number[], fIdx: number[]): FallSite {
    const a = k * RIVER_STRIDE, b = a + RIVER_STRIDE;
    const ax = r[a], az = r[a + 1], bx = r[b], bz = r[b + 1];
    const wa = r[a + 2], wb = r[b + 2];
    const hw = (r[a + 3] + r[b + 3]) * 0.25 + RIBBON_LIP;
    let dx = bx - ax, dz = bz - az;
    const len = Math.hypot(dx, dz) || 1;
    dx /= len; dz /= len;
    const nx = -dz, nz = dx;
    const { lipT } = fallBreak(len);
    const lx = ax + dx * len * lipT, lz = az + dz * len * lipT;
    const drop = Math.max(0.5, wa - wb);
    const { pts, landing } = nappeCurve(drop, NAPPE_V0);
    const topY = wa + RIBBON_LIFT;
    // Rows: the curve, then one row NAPPE_DIP under the pool a little further on.
    const rows = pts.map((p) => ({ s: p.s, y: topY + p.y, v: p.v }));
    const last = pts[pts.length - 1];
    rows.push({ s: last.s + 0.5, y: wb - NAPPE_DIP, v: last.v + Math.hypot(0.5, last.y - (wb - NAPPE_DIP - topY)) });
    const s0 = sPos.length / 3;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const cx = lx + dx * row.s, cz = lz + dz * row.s;
      for (let c = 0; c < NAPPE_COLS; c++) {
        const f = c / (NAPPE_COLS - 1);
        const across = (f - 0.5) * 2 * hw;
        sPos.push(cx + nx * across, row.y, cz + nz * across);
        sUv.push(across, row.v);
      }
      if (i > 0) {
        for (let c = 0; c < NAPPE_COLS - 1; c++) {
          const p0 = s0 + (i - 1) * NAPPE_COLS + c, p1 = p0 + 1, q0 = p0 + NAPPE_COLS, q1 = q0 + 1;
          sIdx.push(p0, q0, p1, p1, q0, q1);
        }
      }
    }
    // The foam ring on the pool around the landing.
    const px = lx + dx * (landing + 0.8), pz = lz + dz * (landing + 0.8), py = wb + 0.06;
    const R = hw + 2.2, N = 16;
    const f0 = fPos.length / 3;
    fPos.push(px, py, pz); fUv.push(0, 0);
    for (let q = 0; q < N; q++) {
      const ang = (q / N) * Math.PI * 2;
      const rr = R * (1 + 0.18 * Math.sin(ang * 3 + landing)); // a ragged rim
      fPos.push(px + Math.cos(ang) * rr, py, pz + Math.sin(ang) * rr); fUv.push(1, q / N);
      fIdx.push(f0, f0 + 1 + ((q + 1) % N), f0 + 1 + q);
    }
    return {
      key: `${ax.toFixed(1)}:${az.toFixed(1)}`, x: lx + dx * landing, y: wb, z: lz + dz * landing,
      lipX: lx, lipY: wa, lipZ: lz, dx, dz, drop, width: hw * 2 - RIBBON_LIP * 2,
      ax, az, bx, bz, wa, wb, len, depthA: r[a + 4], depthB: r[b + 4],
    };
  }

  // ── WATERFALL PARTICLES: spray at the plunge, mist rising off the pool, a
  //    little crest spray at the lip — the engine's particle emitters (GPU
  //    compute on WebGPU, the CPU adapter on WebGL 2), budgets from the fall's
  //    width and drop × the tier's particleCap (0 = none, the weather's law).
  //    The kernels spawn at `worldMatrix` in RENDER space, so every tick the
  //    emitters are re-seated at the site + scene.position (the FP64 rebase).
  private syncFx(): void {
    const cap = activePreset().particleCap ?? 0;
    if (cap <= 0 || !this.app || !this.scene) { this.clearFx(); return; }
    const px = atlas.localX, pz = atlas.localZ;
    const want = this.fallSites.map((site) => ({ site, d: Math.hypot(site.x - px, site.z - pz) }))
      .filter((o) => o.d < FX_RADIUS).sort((p, q) => p.d - q.d).slice(0, FX_MAX);
    const keys = new Set(want.map((o) => o.site.key));
    this.fx = this.fx.filter((f) => { if (keys.has(f.key)) return true; this.removeFx(f); return false; });
    for (const o of want) if (!this.fx.some((f) => f.key === o.site.key)) this.fx.push(this.makeFx(o.site, cap));
  }

  private makeFx(site: FallSite, cap: number): FallFx {
    const recipe = fallFxRecipe(site.width, site.drop, cap) as FallFxRecipe;
    const app = this.app as App;
    const mk = (cfg: Record<string, unknown>): Emitter =>
      app.renderer.isWebGPU ? new GpuParticleEmitter(app.renderer as never, cfg as never) : new CpuParticleAdapter(cfg as never);
    const hw = site.width * 0.5;
    const spray = mk({
      maxParticles: recipe.sprayMax, lifetime: { min: 0.5, max: 1.1 }, startSpeed: { min: 2.5, max: 6 }, startSize: { min: 0.09, max: 0.3 },
      startColor: 0xe4eff9, simulationSpace: 'world', shape: { type: 'cone', angle: 0.55, radius: hw * 0.7 + 0.4, height: 0.4 },
      emission: { rateOverTime: recipe.sprayRate }, forces: { gravityModifier: 1, drag: 0.5 }, blending: THREE.NormalBlending, depthWrite: false,
      sizeOverLifetime: [{ time: 0, value: 0.6 }, { time: 0.35, value: 1 }, { time: 1, value: 0.5 }],
      alphaOverLifetime: [{ time: 0, value: 0.85 }, { time: 0.6, value: 0.7 }, { time: 1, value: 0 }],
    });
    const mist = mk({
      maxParticles: recipe.mistMax, lifetime: { min: 2.4, max: 4.5 }, startSpeed: { min: 0.5, max: 1.4 }, startSize: { min: recipe.mistSize * 0.6, max: recipe.mistSize },
      startColor: 0xe3ecf3, simulationSpace: 'world', shape: { type: 'hemisphere', radius: hw + 1.5 },
      emission: { rateOverTime: recipe.mistRate }, forces: { gravityModifier: -0.03, drag: 0.7, curl: { strength: 0.8, frequency: 0.35, scrollSpeed: 0.25 } },
      blending: THREE.NormalBlending, depthWrite: false,
      sizeOverLifetime: [{ time: 0, value: 0.5 }, { time: 1, value: 1.8 }],
      alphaOverLifetime: [{ time: 0, value: 0 }, { time: 0.25, value: 0.2 }, { time: 1, value: 0 }],
    });
    const crest = mk({
      maxParticles: recipe.crestMax, lifetime: { min: 0.5, max: 0.9 }, startSpeed: { min: 1.2, max: 3.2 }, startSize: { min: 0.06, max: 0.16 },
      startColor: 0xeef5fb, simulationSpace: 'world', shape: { type: 'cone', angle: 0.35, radius: hw * 0.8, height: 0.2 },
      emission: { rateOverTime: recipe.crestRate }, forces: { gravityModifier: 1, drag: 0.2 }, blending: THREE.NormalBlending, depthWrite: false,
    });
    for (const e of [spray, mist, crest]) { e.mesh.frustumCulled = false; e.mesh.renderOrder = 5; (this.scene as THREE.Scene).add(e.mesh); }
    const fx = { key: site.key, site, spray, mist, crest, recipe };
    this.seatFx(fx);
    return fx;
  }

  private seatFx(f: FallFx): void {
    // GPU kernels spawn in RENDER space (the mesh transform is ignored — the
    // weather module's law: site + scene.position); the CPU adapter's instances
    // are children of the scene, so they take ABSOLUTE coordinates like every
    // other river mesh. Seated by backend, or the WebGL 2 spray lands 500 km off.
    const sp = f.spray.backend === 'gpu' ? (this.scene as THREE.Scene).position : this.fxZero;
    this.fxPos.set(f.site.x + sp.x, f.site.y + sp.y, f.site.z + sp.z);
    f.spray.worldMatrix.setPosition(this.fxPos);
    f.mist.worldMatrix.setPosition(this.fxPos);
    // The crest cone leans downstream and up: +Y → dir + 0.7·up.
    this.fxDir.set(f.site.dx, 0.7, f.site.dz).normalize();
    this.fxQuat.setFromUnitVectors(this.fxUp, this.fxDir);
    this.fxPos.set(f.site.lipX + sp.x, f.site.lipY + sp.y, f.site.lipZ + sp.z);
    f.crest.worldMatrix.compose(this.fxPos, this.fxQuat, this.fxScale);
  }

  private stepFx(dt: number): void {
    if (this.fx.length === 0) { if (this.fallSites.length && ++this.fxTick % FX_TICK === 0) this.syncFx(); return; }
    if (++this.fxTick % FX_TICK === 0) this.syncFx();
    const d = Math.min(0.1, Math.max(0, dt));
    for (const f of this.fx) {
      this.seatFx(f); f.spray.update(d); f.mist.update(d); f.crest.update(d);
      // The plunge churns the pool every frame (a no-op outside the field's window).
      const ps = plungeStamp(f.site.width, f.site.drop);
      stampWater(f.site.x, f.site.z, ps.strength, ps.radius);
    }
  }

  private removeFx(f: FallFx): void {
    for (const e of [f.spray, f.mist, f.crest]) { this.scene?.remove(e.mesh); disposeLater(e); }
  }

  private clearFx(): void {
    for (const f of this.fx) this.removeFx(f);
    this.fx = [];
  }

  /** Probe: the falls in the build and the particle sets riding the nearest ones. */
  private fallState(): unknown {
    return {
      sites: this.fallSites.length, radius: FX_RADIUS, particleCap: activePreset().particleCap ?? 0,
      fx: this.fx.map((f) => ({ key: f.key, drop: f.site.drop, width: f.site.width, d: Math.hypot(f.site.x - atlas.localX, f.site.z - atlas.localZ), backend: f.spray.backend, recipe: f.recipe, alive: { spray: f.spray.getActiveCount(), mist: f.mist.getActiveCount(), crest: f.crest.getActiveCount() } })),
    };
  }

  /**
   * Probe: samples along every fall reach within `radius` — the carve's water,
   * the ribbon's surface where the ribbon exists (null in the sheet's zone
   * between the lip and the foot) and the sheet's landing — so the fit probe
   * can hold the RIBBON against the chunk mesh between the river's points.
   */
  private fallSamples(radius: number): Array<{ key: string; t: number; x: number; z: number; water: number; ribbon: number | null; depth: number; drop: number }> {
    const out: Array<{ key: string; t: number; x: number; z: number; water: number; ribbon: number | null; depth: number; drop: number }> = [];
    for (const site of this.fallSites) {
      if (Math.hypot(site.x - atlas.localX, site.z - atlas.localZ) > radius) continue;
      const { lipT, footT } = fallBreak(site.len);
      for (let i = 1; i < 20; i++) {
        const t = i / 20;
        const x = site.ax + (site.bx - site.ax) * t, z = site.az + (site.bz - site.az) * t;
        const water = reachWater(site.wa, site.wb, site.len, true, t);
        const ribbon = t <= lipT ? site.wa : t >= footT ? site.wb : null;
        out.push({ key: site.key, t, x, z, water, ribbon, depth: site.depthA + (site.depthB - site.depthA) * t, drop: site.drop });
      }
    }
    return out;
  }

  dispose(): void {
    this.clearFx();
    for (const m of [this.ribbon, this.sheets, this.foam]) {
      if (!m) continue;
      this.scene?.remove(m);
      disposeLater(m.geometry);
      disposeLater(m.material as THREE.Material);
    }
    this.ribbon = this.sheets = this.foam = null;
    if (this.riverHandles) { disposeLater(this.riverHandles.material); this.riverHandles = null; }
    if (this.simpleMat) { disposeLater(this.simpleMat); this.simpleMat = null; }
    if (this.normalTex) { disposeLater(this.normalTex); this.normalTex = null; }
    void this.app;
  }
}
